import { observer } from "mobx-react-lite";

import { Badge } from "@/components/ui/badge";
import { useTran } from "@/hooks";
import { useOrganizationStore } from "../hooks";
import { getUnitTypeMeta } from "../utils/organization.helpers";

const LEVEL_COLORS = [
  "from-blue-500/90 to-blue-500/70 text-white",
  "from-amber-500/90 to-amber-500/70 text-white",
  "from-indigo-500/90 to-indigo-500/70 text-white",
];

const LEVEL_COLORS_BUSINESS_TYPE = [
  "from-blue-500/90 to-blue-500/70 text-white",
  "from-emerald-500/90 to-emerald-500/70 text-white",
  "from-amber-500/90 to-amber-500/70 text-white",
  "from-indigo-500/90 to-indigo-500/70 text-white",
];

const LEVEL_REGEX = /^level_(\d+)$/;

const getLevelNumber = (unitLevel) => {
  if (!unitLevel) return null;
  const match = LEVEL_REGEX.exec(unitLevel);
  if (!match) return null;
  const level = Number(match[1]);
  return Number.isFinite(level) && level > 0 ? level : null;
};

const buildGroupedDiagramTree = (tree = []) => {
  if (!Array.isArray(tree) || tree.length === 0) return tree;

  return tree.map((root) => {
    const groupNodes = Array.isArray(root.children) ? root.children : [];

    const nextGroupNodes = groupNodes.map((group) => {
      const level1Entries = new Map();

      const registerLevel1 = (node) => {
        if (!node || level1Entries.has(node.id)) return;
        level1Entries.set(node.id, { node, listItems: [] });
      };

      const visit = (node, currentLevel1) => {
        if (!node) return;
        const level = getLevelNumber(node.unitLevel);

        if (level === 1) {
          currentLevel1 = node;
          registerLevel1(node);
        } else if (level && level >= 2 && currentLevel1) {
          const entry = level1Entries.get(currentLevel1.id);
          if (entry) entry.listItems.push(node);
        }

        if (Array.isArray(node.children) && node.children.length) {
          node.children.forEach((child) => visit(child, currentLevel1));
        }
      };

      (group.children || []).forEach((child) => visit(child, null));

      const level1Nodes = Array.from(level1Entries.values()).map(
        ({ node, listItems }) => ({
          ...node,
          children: [],
          listItems,
        }),
      );

      return {
        ...group,
        children: level1Nodes,
      };
    });

    return {
      ...root,
      children: nextGroupNodes,
    };
  });
};

const resolveLevelColors = (filter) => {
  if (filter === "level") return LEVEL_COLORS;
  return LEVEL_COLORS_BUSINESS_TYPE;
};

const DiagramNode = ({ node, level = 0, filter }) => {
  const { t } = useTran();
  const meta = getUnitTypeMeta(node.unitLevel);
  const hasChildren = Array.isArray(node.children) && node.children.length > 0;
  const colors = resolveLevelColors(filter);
  const colorClass = colors[level % colors.length];
  const locationLabel =
    node.locationLabel || node.countryLabel || node.country || node.regionLabel;
  const listItems = Array.isArray(node.listItems) ? node.listItems : [];
  const listIsScrollable = filter !== "level";
  const listContainerClass = `w-full rounded-radius border border-border bg-background/80 px-3 py-2 text-left text-xs text-muted-foreground shadow-sm ${
    listIsScrollable ? "max-h-32 overflow-y-auto pr-2" : ""
  }`;

  return (
    <div className="flex min-w-[150px] flex-col items-center gap-2">
      <div
        className={`w-full rounded-radius bg-gradient-to-r ${colorClass} px-3 py-2 text-center shadow-sm`}
      >
        <p className="truncate text-sm font-semibold">{node.name}</p>
        <div className="mt-1 flex flex-wrap items-center justify-center gap-1 text-[11px]">
          <Badge
            variant="outline"
            className="border-white/30 bg-white/20 px-2 text-white shadow-sm"
          >
            {t(meta.label)}
          </Badge>
          {locationLabel ? (
            <Badge
              variant="outline"
              className="border-slate-100/50 bg-slate-900/30 px-2 text-slate-100"
            >
              {locationLabel}
            </Badge>
          ) : null}
        </div>
      </div>

      {listItems.length > 0 ? (
        <div className={listContainerClass}>
          <ul className="space-y-1">
            {listItems.map((item) => (
              <li key={item.id || item.name} className="flex items-start gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-muted-foreground/60" />
                <span className="text-foreground">{item.name}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {hasChildren ? (
        <div className="flex flex-col items-center gap-2">
          <div className="h-4 w-px bg-border" />
          <div className="relative flex flex-wrap items-start justify-center gap-4 px-2">
            <div className="absolute left-0 right-0 top-2 h-px bg-border" />
            {node.children.map((child) => (
              <div
                key={child.id || child.name}
                className="flex flex-col items-center"
              >
                <div className="mb-2 h-2 w-px bg-border" />
                <DiagramNode node={child} level={level + 1} filter={filter} />
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const LoadingState = () => (
  <div className="flex min-w-[180px] flex-col items-center gap-3">
    <div className="h-16 w-full animate-pulse rounded-radius bg-muted/70" />
    <div className="h-5 w-1/2 animate-pulse rounded-full bg-muted/60" />
  </div>
);

const EmptyState = () => {
  const { t } = useTran();
  return (
    <div className="flex h-32 w-full items-center justify-center rounded-radius border border-dashed border-border text-sm text-muted-foreground">
      {t(
        "No units to visualize yet. Add a unit to see the relationship diagram.",
      )}
    </div>
  );
};

const OrganizationDiagram = observer(({ tree: treeOverride, groupMode }) => {
  const { tree: storeTree, loading } = useOrganizationStore();
  const tree = treeOverride ?? storeTree;
  const diagramTree =
    groupMode === "businessType" || groupMode === "country"
      ? buildGroupedDiagramTree(tree)
      : tree;

  return (
    <div className="relative overflow-auto">
      <div className="min-w-full space-y-4">
        {loading ? (
          <div className="flex flex-wrap justify-center gap-6">
            {Array.from({ length: 3 }).map((_, idx) => (
              <LoadingState key={idx} />
            ))}
          </div>
        ) : null}

        {!loading && (!diagramTree || diagramTree.length === 0) ? (
          <EmptyState />
        ) : null}

        {!loading && diagramTree && diagramTree.length > 0 ? (
          <div className="flex flex-col items-center gap-6">
            {diagramTree.map((root) => (
              <DiagramNode
                key={root.id || root.name}
                node={root}
                level={0}
                filter={groupMode}
              />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
});

export default OrganizationDiagram;
